/**
 * Frontend SSOT — Search Contract & Decision Matrix (Faza 0 / Faza 2).
 * Katalog geo/typów można nadpisać z PHP `SearchPresenter` via {@see hydrateSearchCatalog}.
 * @see Specyfikacja/faza-0-kontrakt-search-routing.md
 */

const catalog = {
  polandDest: new Set(['poland', 'zakopane-harenda']),
  foreignParents: new Set(['italy', 'austria']),
  foreignResorts: new Set([
    'livigno', 'passo-del-tonale', 'kronplatz', 'val-gardena', 'madonna-di-campiglio',
    'andalo', 'maso-corto', 'passo-san-pellegrino',
    'vorarlberg', 'salzburg',
  ]),
  lessonTypes: new Set(['lekcje-dzieci', 'lekcje-dorosli']),
  polandTypes: [
    'klub-pingwina',
    'lekcje-dzieci',
    'tyczki-dzieci',
    'akademia-podhala',
    'lekcje-dorosli',
    'kursy-dorosli',
    'tyczki-dorosli',
    'kurs-instruktora',
    'kurs-stopnie-instruktora',
  ],
  foreignTraining: new Set([
    'klub-pingwina',
    'lekcje-dzieci',
    'lekcje-dorosli',
    'kursy-dorosli',
  ]),
  childrenTypes: new Set([
    'klub-pingwina',
    'lekcje-dzieci',
    'tyczki-dzieci',
    'akademia-podhala',
    'kurs-instruktora',
  ]),
  adultTypes: new Set([
    'lekcje-dorosli',
    'kursy-dorosli',
    'tyczki-dorosli',
    'kurs-stopnie-instruktora',
  ]),
  /** Foreign UI types including hotel package (unified search). */
  foreignTypes: [
    'wyjazd-hotel',
    'klub-pingwina',
    'lekcje-dzieci',
    'lekcje-dorosli',
    'kursy-dorosli',
  ],
  productPaths: {},
  listingModes: {
    'wyjazd-hotel': 'inventory',
    'klub-pingwina': 'direction_first',
    'lekcje-dzieci': 'product',
    'lekcje-dorosli': 'product',
    'tyczki-dzieci': 'landing',
    'tyczki-dorosli': 'landing',
    'szkoly-kolonie': 'landing',
    'akademia-podhala': 'landing',
    'kurs-instruktora': 'landing',
    'kurs-stopnie-instruktora': 'landing',
    'kursy-dorosli': 'direction_first',
  },
  unifiedEnabled: true,
  urls: {
    children: '/lista-szkolen-dzieci/',
    adults: '/lista-szkolen-dorosli/',
    travel: '/oferta/',
    oferty: '/oferty/',
    lessonProduct: '/lekcje-indywidualne-narty/',
    lessonSnowboardProduct: '/lekcje-indywidualne-snowboard/',
    pingwin: '/klub-pingwina/',
    adultsCourses: '/kursy-dorosli/',
    contact: '/kontakt/',
  },
};

const toStringList = (value) => (
  Array.isArray(value) ? value.map(String).filter(Boolean) : []
);

/**
 * Hydrate FE catalog from SearchPresenter / REST `search-ui` payload.
 * @param {Record<string, any>} ui
 */
export const hydrateSearchCatalog = (ui = {}) => {
  if (!ui || typeof ui !== 'object') return;

  const geo = ui.geo || {};
  const poland = toStringList(geo.poland);
  const parents = toStringList(geo.foreign_parents);
  const resorts = toStringList(geo.foreign_resorts);
  if (poland.length) catalog.polandDest = new Set(poland);
  if (parents.length) catalog.foreignParents = new Set(parents);
  if (resorts.length) catalog.foreignResorts = new Set(resorts);

  const types = ui.types || {};
  const lesson = toStringList(types.lesson_slugs);
  const polandTypes = toStringList(types.poland_slugs);
  const foreignTraining = toStringList(types.foreign_training_slugs);
  const children = toStringList(types.children_slugs);
  const adults = toStringList(types.adult_slugs);
  if (lesson.length) catalog.lessonTypes = new Set(lesson);
  if (polandTypes.length) catalog.polandTypes = polandTypes;
  if (foreignTraining.length) catalog.foreignTraining = new Set(foreignTraining);
  const foreignAll = toStringList(types.foreign_slugs);
  if (foreignAll.length) catalog.foreignTypes = foreignAll;
  if (children.length) catalog.childrenTypes = new Set(children);
  if (adults.length) catalog.adultTypes = new Set(adults);

  if (types.listing_modes && typeof types.listing_modes === 'object') {
    catalog.listingModes = { ...catalog.listingModes, ...types.listing_modes };
  }
  if (ui.unified?.listing_modes && typeof ui.unified.listing_modes === 'object') {
    catalog.listingModes = { ...catalog.listingModes, ...ui.unified.listing_modes };
  }
  if (typeof ui.unified?.enabled === 'boolean') {
    catalog.unifiedEnabled = ui.unified.enabled;
  }

  const catalogItems = Array.isArray(types.catalog) ? types.catalog : [];
  if (catalogItems.length) {
    const paths = {};
    catalogItems.forEach((item) => {
      const slug = String(item?.slug || '').trim();
      const path = String(item?.product_path || '').trim();
      if (slug && path) paths[slug] = path;
    });
    catalog.productPaths = paths;
  }

  const urls = ui.urls || {};
  catalog.urls = {
    children: urls.lista_dzieci || catalog.urls.children,
    adults: urls.lista_dorosli || catalog.urls.adults,
    travel: urls.oferta || catalog.urls.travel,
    oferty: urls.oferty || catalog.urls.oferty || '/oferty/',
    lessonProduct: urls.lekcje || catalog.urls.lessonProduct,
    lessonSnowboardProduct: urls.lekcje_snowboard || catalog.urls.lessonSnowboardProduct,
    pingwin: urls.klub_pingwina || catalog.urls.pingwin || '/klub-pingwina/',
    adultsCourses: urls.kursy_dorosli || catalog.urls.adultsCourses || '/kursy-dorosli/',
    contact: urls.kontakt || catalog.urls.contact || '/kontakt/',
  };

  // Keep exported aliases in sync for consumers / tests.
  POLAND_TYPE_SLUGS.splice(0, POLAND_TYPE_SLUGS.length, ...catalog.polandTypes);
  FOREIGN_TRAINING_TYPE_SLUGS.splice(0, FOREIGN_TRAINING_TYPE_SLUGS.length, ...catalog.foreignTraining);
  INDIVIDUAL_LESSON_TYPES.clear();
  catalog.lessonTypes.forEach((slug) => INDIVIDUAL_LESSON_TYPES.add(slug));
  CHILDREN_TYPE_SLUGS.clear();
  catalog.childrenTypes.forEach((slug) => CHILDREN_TYPE_SLUGS.add(slug));
  ADULT_TYPE_SLUGS.clear();
  catalog.adultTypes.forEach((slug) => ADULT_TYPE_SLUGS.add(slug));
};

export const getSearchCatalogUrls = () => ({ ...catalog.urls });

export const LISTING_MODE = {
  INVENTORY: 'inventory',
  PRODUCT: 'product',
  LANDING: 'landing',
  DIRECTION_FIRST: 'direction_first',
};

export const HOTEL_TYPE_SLUG = 'wyjazd-hotel';

export const getListingMode = (slug) => (
  catalog.listingModes[slug] || LISTING_MODE.PRODUCT
);

/**
 * @param {string[]} types
 * @returns {string}
 */
export const primaryListingMode = (types = []) => {
  if (!types.length) return LISTING_MODE.INVENTORY;
  const modes = new Set(types.map(getListingMode));
  if (modes.has(LISTING_MODE.DIRECTION_FIRST)) return LISTING_MODE.DIRECTION_FIRST;
  if (modes.has(LISTING_MODE.LANDING)) return LISTING_MODE.LANDING;
  if (modes.has(LISTING_MODE.PRODUCT)) return LISTING_MODE.PRODUCT;
  return LISTING_MODE.INVENTORY;
};

/**
 * Unified search: when to show Kiedy / Ile osób on the search bar / listing filters.
 * @param {'any'|'poland'|'foreign'|'invalid'} mode
 * @param {string[]} types
 */
export const shouldShowDateGuests = (mode, types = []) => {
  if (mode !== 'poland' && mode !== 'foreign') return false;

  // PL i zagranica bez typu — najpierw Typ (ofertaTypowWymaga). „Dowolna oferta” = pusty typ.
  if (!Array.isArray(types) || !types.length) return false;

  const listing = primaryListingMode(types);
  // Landing / product / direction_first → termin na stronie produktu (także zagranica).
  // Daty w pasku tylko dla inventarza (wyjazd-hotel).
  if (
    listing === LISTING_MODE.LANDING
    || listing === LISTING_MODE.PRODUCT
    || listing === LISTING_MODE.DIRECTION_FIRST
  ) {
    return false;
  }
  return true;
};

/** Spec `ofertaTypowWymaga`: PL i zagranica wymagają realnego typu (nie „Dowolna oferta”) przed Szukaj / Kiedy / Osoby. */
export const shouldRequireOfferType = (mode, types = []) => (
  (mode === 'poland' || mode === 'foreign')
  && (!Array.isArray(types) || types.length === 0)
);

export const INTENT = {
  LESSON: 'lesson',
  SCHOOL: 'school',
  PACKAGE: 'package',
  TRAINING: 'training',
  INVALID: 'invalid',
};

export const INDIVIDUAL_LESSON_TYPES = new Set(['lekcje-dzieci', 'lekcje-dorosli']);

export const POLAND_TYPE_SLUGS = [
  'klub-pingwina',
  'lekcje-dzieci',
  'tyczki-dzieci',
  'akademia-podhala',
  'lekcje-dorosli',
  'kursy-dorosli',
  'tyczki-dorosli',
  'kurs-instruktora',
  'kurs-stopnie-instruktora',
];

export const FOREIGN_TRAINING_TYPE_SLUGS = [
  'klub-pingwina',
  'lekcje-dzieci',
  'lekcje-dorosli',
  'kursy-dorosli',
];

export const CHILDREN_TYPE_SLUGS = new Set([
  'klub-pingwina',
  'lekcje-dzieci',
  'tyczki-dzieci',
  'akademia-podhala',
  'kurs-instruktora',
]);

export const ADULT_TYPE_SLUGS = new Set([
  'lekcje-dorosli',
  'kursy-dorosli',
  'tyczki-dorosli',
  'kurs-stopnie-instruktora',
]);

export const INSTRUCTOR_TYPE_SLUGS = new Set(['kurs-instruktora', 'kurs-stopnie-instruktora']);

export const isPolandDestination = (slug) => catalog.polandDest.has(slug);

export const isForeignDestination = (slug) => (
  catalog.foreignParents.has(slug) || catalog.foreignResorts.has(slug)
);

/** Resort → kraj (SSOT jak SearchContract::DESTINATION_PARENT). */
const DESTINATION_PARENT = {
  'zakopane-harenda': 'poland',
  livigno: 'italy',
  'passo-del-tonale': 'italy',
  kronplatz: 'italy',
  'val-gardena': 'italy',
  'madonna-di-campiglio': 'italy',
  andalo: 'italy',
  'maso-corto': 'italy',
  'passo-san-pellegrino': 'italy',
  vorarlberg: 'austria',
  salzburg: 'austria',
};

/**
 * Slug kraju dla kierunku (parent albo resort).
 * @returns {string|null}
 */
export const destinationCountrySlug = (slug) => {
  const value = String(slug || '');
  if (!value) return null;
  if (catalog.polandDest.has(value) || value === 'poland') return 'poland';
  if (catalog.foreignParents.has(value)) return value;
  if (DESTINATION_PARENT[value]) return DESTINATION_PARENT[value];
  return null;
};

/**
 * @returns {'any'|'poland'|'foreign'|'invalid'}
 */
export const getDestinationMode = (destinations = []) => {
  if (!destinations.length) return 'any';

  const countries = new Set();
  let hasPoland = false;
  let hasForeign = false;

  for (const slug of destinations) {
    if (isPolandDestination(slug)) {
      hasPoland = true;
      countries.add('poland');
      continue;
    }
    if (isForeignDestination(slug)) {
      hasForeign = true;
      const country = destinationCountrySlug(slug);
      if (!country) return 'invalid';
      countries.add(country);
      continue;
    }
    return 'invalid';
  }

  // Max 1 kraj (kilka ośrodków OK; Włochy+Austria / PL+zagranica = invalid).
  if (countries.size > 1) return 'invalid';
  if (hasPoland && hasForeign) return 'invalid';
  if (hasPoland) return 'poland';
  if (hasForeign) return 'foreign';
  return 'invalid';
};

export const filterTypesForMode = (mode, types = []) => {
  if (mode === 'foreign') {
    const allowed = new Set(catalog.foreignTypes);
    return types.filter((slug) => allowed.has(slug) || catalog.foreignTraining.has(slug));
  }
  if (mode === 'poland') {
    const allowed = new Set(catalog.polandTypes);
    return types.filter((slug) => allowed.has(slug));
  }
  return [];
};

export const hasMixedSchoolAudience = (types = []) => {
  let hasChildren = false;
  let hasAdults = false;

  types.forEach((slug) => {
    if (catalog.childrenTypes.has(slug)) hasChildren = true;
    if (catalog.adultTypes.has(slug)) hasAdults = true;
  });

  return hasChildren && hasAdults;
};

export const isOnlyLessonTypes = (types = []) => (
  types.length > 0 && types.every((slug) => catalog.lessonTypes.has(slug))
);

const parseIso = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatIso = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

/**
 * Decision Matrix §1.1 — returns intent + matrix row reason.
 * @returns {{ intent: string, reason: string, matrixRow: number|null }}
 */
export const resolveIntent = (mode, types = []) => {
  // Row 11 — no destination → mode any
  if (mode === 'any') {
    return { intent: INTENT.INVALID, reason: 'missing destination', matrixRow: 11 };
  }

  // Row 10 — PL + Foreign (or unknown slug)
  if (mode === 'invalid') {
    return { intent: INTENT.INVALID, reason: 'invalid destination combination', matrixRow: 10 };
  }

  // Row 1 / 7 — only lesson types (any valid mode)
  if (isOnlyLessonTypes(types)) {
    return {
      intent: INTENT.LESSON,
      reason: mode === 'foreign' ? 'foreign lessons → lesson product' : 'poland lessons → lesson product',
      matrixRow: mode === 'foreign' ? 7 : 1,
    };
  }

  if (mode === 'poland') {
    // Browse by destination — all PL catalogue when type empty.
    if (!types.length) {
      return { intent: INTENT.SCHOOL, reason: 'poland browse (no type filter)', matrixRow: 5 };
    }
    // Row 4
    if (hasMixedSchoolAudience(types)) {
      return { intent: INTENT.INVALID, reason: 'poland mixed children+adults audience', matrixRow: 4 };
    }
    // Row 2 / 3
    return { intent: INTENT.SCHOOL, reason: 'poland school catalogue', matrixRow: 2 };
  }

  if (mode === 'foreign') {
    // Row 6 — browse: hotele + katalog (Pingwin/lekcje/kursy) bez filtra typu
    if (!types.length) {
      return { intent: INTENT.PACKAGE, reason: 'foreign browse (no type filter)', matrixRow: 6 };
    }
    // Row 8 — remaining foreign training types (non-lesson-only)
    return { intent: INTENT.TRAINING, reason: 'foreign training-only listing', matrixRow: 8 };
  }

  return { intent: INTENT.INVALID, reason: 'unresolved intent', matrixRow: null };
};

/** @deprecated Use resolveIntent(...).intent — kept for callers expecting a string. */
export const resolveIntentSlug = (mode, types = []) => resolveIntent(mode, types).intent;

export const getSearchContext = (destinations = [], options = {}) => {
  const { types = [] } = options;
  const mode = getDestinationMode(destinations);

  if (mode === 'poland') {
    return { mode, tripMode: 'training-only', showTypes: true };
  }

  if (mode === 'foreign') {
    return {
      mode,
      tripMode: types.length ? 'training-only' : 'package',
      showTypes: true,
    };
  }

  return { mode, tripMode: 'any', showTypes: mode !== 'invalid' };
};

export const getVisibleTypeSlugs = (context) => {
  if (context.mode === 'poland') return new Set(catalog.polandTypes);
  if (context.mode === 'foreign') return new Set(catalog.foreignTypes);
  return new Set();
};

export const allowsSingleDaySelection = (types = []) => (
  types.some((slug) => catalog.lessonTypes.has(slug))
);

export const FERIE_GROUPS = [
  {
    id: 'ice',
    tone: 'ice',
    start: '2026-01-19',
    end: '2026-02-01',
    label: '19 sty - 1 lut 2026',
    voivodeships: 'lubelskie, łódzkie, podkarpackie, pomorskie, śląskie',
  },
  {
    id: 'blue',
    tone: 'blue',
    start: '2026-02-02',
    end: '2026-02-15',
    label: '2 - 15 lut 2026',
    voivodeships: 'dolnośląskie, kujawsko-pomorskie, łódzkie, zachodniopomorskie, małopolskie, opolskie',
  },
  {
    id: 'orange',
    tone: 'orange',
    start: '2026-02-16',
    end: '2026-03-01',
    label: '16 lut - 1 mar 2026',
    voivodeships: 'podkarpackie, lubelskie, wielkopolskie, lubuskie, śląskie',
  },
];

export const COURSE_PRESETS = [
  { id: 'opening', start: '2025-12-14', end: '2025-12-18', label: '14 - 18 gru 2025' },
  { id: 'pre-xmas', start: '2025-12-18', end: '2025-12-22', label: '18 - 22 gru 2025' },
  { id: 'sylwester', start: '2025-12-27', end: '2025-12-31', label: '27 - 31 gru 2025' },
  { id: 'new-year', start: '2026-01-03', end: '2026-01-06', label: '3 - 6 sty 2026' },
  { id: 'pre-holiday', start: '2026-01-05', end: '2026-01-10', label: '5 - 10 sty 2026' },
];

export const generateFerieTurnusy = (group) => {
  const slots = [];
  const periodStart = parseIso(group.start);
  const periodEnd = parseIso(group.end);
  if (!periodStart || !periodEnd) return slots;

  const cursor = new Date(periodStart);
  while (cursor <= periodEnd) {
    if (cursor.getDay() === 1) {
      const saturday = new Date(cursor);
      saturday.setDate(saturday.getDate() + 5);
      if (saturday <= periodEnd) {
        slots.push({
          start: formatIso(cursor),
          end: formatIso(saturday),
          tone: group.tone,
          ferieId: group.id,
          label: `${cursor.getDate()}.${String(cursor.getMonth() + 1).padStart(2, '0')} - ${saturday.getDate()}.${String(saturday.getMonth() + 1).padStart(2, '0')}.${saturday.getFullYear()}`,
        });
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return slots;
};

export const buildDefaultTurnusSlots = () => {
  const slots = COURSE_PRESETS.map((preset) => ({
    ...preset,
    tone: 'default',
  }));

  FERIE_GROUPS.forEach((group) => {
    slots.push(...generateFerieTurnusy(group));
  });

  return slots;
};

export const mergeTurnusSlots = (dynamicSlots = [], { includeDefaults = true } = {}) => {
  const map = new Map();
  const base = includeDefaults ? buildDefaultTurnusSlots() : [];

  [...base, ...dynamicSlots].forEach((slot) => {
    if (!slot?.start || !slot?.end) return;
    const key = `${slot.start}:${slot.end}`;
    if (!map.has(key)) map.set(key, slot);
  });

  return [...map.values()].sort((a, b) => String(a.start).localeCompare(String(b.start)));
};

export const findTurnusForDate = (date, slots = []) => {
  if (!date) return null;
  const time = date.getTime();

  return slots.find((slot) => {
    const start = parseIso(slot.start);
    const end = parseIso(slot.end);
    if (!start || !end) return false;
    return time >= start.getTime() && time <= end.getTime();
  }) || null;
};

export const resolveSchoolListUrl = (types = [], baseUrls = {}) => {
  const childrenUrl = baseUrls.children || '/lista-szkolen-dzieci/';
  const adultsUrl = baseUrls.adults || '/lista-szkolen-dorosli/';

  if (!types.length) return childrenUrl;

  const hasChildren = types.some((slug) => CHILDREN_TYPE_SLUGS.has(slug));
  const hasAdults = types.some((slug) => ADULT_TYPE_SLUGS.has(slug) || INSTRUCTOR_TYPE_SLUGS.has(slug));

  if (hasAdults && !hasChildren) return adultsUrl;
  return childrenUrl;
};

export const resolveTravelListUrl = (baseUrl = '/oferta/') => baseUrl;

const defaultSearchUrls = (baseUrls = {}) => ({
  children: baseUrls.children || '/lista-szkolen-dzieci/',
  adults: baseUrls.adults || '/lista-szkolen-dorosli/',
  travel: baseUrls.travel || '/oferta/',
  oferty: baseUrls.oferty || catalog.urls.oferty || '/oferty/',
  lessonProduct: baseUrls.lessonProduct || catalog.urls.lessonProduct || '/lekcje-indywidualne-narty/',
  lessonSnowboardProduct: baseUrls.lessonSnowboardProduct || catalog.urls.lessonSnowboardProduct || '/lekcje-indywidualne-snowboard/',
});

/**
 * Snapshot key for destination/type — used to detect upstream changes (I3).
 */
export const captureSelection = (destinations = [], types = []) => ({
  destinationKey: [...destinations].map(String).sort().join('\0'),
  typeKey: [...types].map(String).sort().join('\0'),
});

/**
 * Clear date/guests only when destination or type actually changed (not on first paint).
 */
export const shouldResetDownstream = (previousSelection, nextSelection) => {
  if (!previousSelection || !nextSelection) return false;
  return previousSelection.destinationKey !== nextSelection.destinationKey
    || previousSelection.typeKey !== nextSelection.typeKey;
};

/**
 * Unified search: valid → `/oferty/`. Legacy intent/flags kept for BE transition.
 */
export const resolveFormAction = (destinations = [], types = [], baseUrls = {}) => {
  const urls = defaultSearchUrls(baseUrls);
  const mode = getDestinationMode(destinations);

  let normalizedTypes = filterTypesForMode(mode, types);
  if (mode === 'foreign' && types.length && normalizedTypes.length < types.length && !normalizedTypes.length) {
    normalizedTypes = [];
  }

  const typesForIntent = normalizedTypes.filter((slug) => slug !== HOTEL_TYPE_SLUG);
  const onlyHotel = normalizedTypes.length > 0
    && normalizedTypes.every((slug) => slug === HOTEL_TYPE_SLUG);

  const resolved = resolveIntent(mode, onlyHotel ? [] : typesForIntent);
  const intent = resolved.intent;

  const decision = {
    intent,
    mode,
    destinations: [...destinations],
    types: (intent === INTENT.PACKAGE || onlyHotel)
      ? (onlyHotel ? [HOTEL_TYPE_SLUG] : [])
      : normalizedTypes,
    action: '',
    valid: intent !== INTENT.INVALID,
    reason: resolved.reason,
    matrixRow: resolved.matrixRow,
    listingMode: primaryListingMode(normalizedTypes),
    showDateGuests: shouldShowDateGuests(mode, normalizedTypes),
    flags: {
      packageOnly: false,
      trainingOnly: false,
    },
    selection: captureSelection(destinations, normalizedTypes),
  };

  if (!decision.valid) {
    return decision;
  }

  if (catalog.unifiedEnabled) {
    decision.flags.packageOnly = onlyHotel;
    decision.flags.trainingOnly = intent === INTENT.TRAINING && onlyHotel;

    if (onlyHotel || normalizedTypes.length === 0) {
      decision.action = urls.oferty;
      return decision;
    }

    if (normalizedTypes.length === 1) {
      decision.action = resolveProductUrlForType(normalizedTypes[0], baseUrls);
      return decision;
    }

    decision.action = urls.oferty;
    return decision;
  }

  switch (intent) {
    case INTENT.LESSON:
      decision.action = urls.lessonProduct;
      break;
    case INTENT.SCHOOL:
      decision.action = resolveSchoolListUrl(decision.types, urls);
      break;
    case INTENT.PACKAGE:
      decision.action = resolveTravelListUrl(urls.travel);
      decision.flags.packageOnly = true;
      break;
    case INTENT.TRAINING:
      decision.action = resolveTravelListUrl(urls.travel);
      decision.flags.trainingOnly = true;
      break;
    case INTENT.INVALID:
    default:
      decision.action = '';
      break;
  }

  return decision;
};

/** @deprecated Alias — use resolveFormAction. */
export const resolveSearchDecision = resolveFormAction;

/**
 * Pure transition for the search state machine.
 * @returns {{ decision: object, resetDownstream: boolean, selection: object }}
 */
export const transitionSearchRules = (previousSelection, destinations, types, baseUrls = {}) => {
  const decision = resolveFormAction(destinations, types, baseUrls);
  return {
    decision,
    selection: decision.selection,
    resetDownstream: shouldResetDownstream(previousSelection, decision.selection),
  };
};

/**
 * TypeRouteMap — product URL for a school card type (list → product).
 */
export const isListingSearchAction = (action, baseUrls = {}) => {
  const listing = String(defaultSearchUrls(baseUrls).oferty || '/oferty/').replace(/\/$/, '');
  const target = String(action || '').replace(/\/$/, '');
  return target === listing;
};

export const resolveProductUrlForType = (type, baseUrls = {}) => {
  const urls = defaultSearchUrls(baseUrls);
  // Lekcje: dyscyplina przed productPaths z katalogu (ten zawsze wskazuje stronę nart).
  if (INDIVIDUAL_LESSON_TYPES.has(type)) {
    const disc = String(baseUrls.discipline || catalog.urls.discipline || '').toLowerCase();
    if (disc === 'snowboard' || disc === 'sb' || disc === 'board') {
      return urls.lessonSnowboardProduct || urls.lessonProduct;
    }
    return urls.lessonProduct || catalog.productPaths?.[type] || '/lekcje-indywidualne-narty/';
  }

  const fromCatalog = catalog.productPaths?.[type];
  if (fromCatalog) return fromCatalog;

  if (type === 'klub-pingwina') return baseUrls.pingwin || catalog.urls.pingwin || '/klub-pingwina/';
  if (type === 'kursy-dorosli') return baseUrls.adultsCourses || catalog.urls.adultsCourses || '/kursy-dorosli/';
  if (type === 'tyczki-dzieci') return '/trening-na-tyczkach/?type[]=tyczki-dzieci';
  if (type === 'tyczki-dorosli') return '/trening-na-tyczkach/?type[]=tyczki-dorosli';
  if (type === 'szkoly-kolonie') return '/szkoly-kolonie/';
  if (type === 'kurs-instruktora') return '/kurs-na-instruktora/?type[]=kurs-instruktora';
  if (type === 'kurs-stopnie-instruktora') return '/kurs-na-instruktora/?type[]=kurs-stopnie-instruktora';
  if (type === 'akademia-podhala') return '/akademia-podhala/';
  return baseUrls.contact || catalog.urls.contact || '/kontakt/';
};

export const audienceForType = (slug) => {
  if (CHILDREN_TYPE_SLUGS.has(slug)) return 'children';
  if (ADULT_TYPE_SLUGS.has(slug) || slug === 'kurs-stopnie-instruktora') return 'adults';
  return 'all';
};
